import type { InputHTMLAttributes, JSX, ReactNode, TextareaHTMLAttributes } from 'react'
import { useId, useState } from 'react'
import { Eye, EyeOff } from 'lucide-react'
import { cn } from '@/lib/cn'

/**
 * Поля ввода. Правила, зашитые в примитив:
 *  - подпись ВИДИМАЯ и связана через `htmlFor` — placeholder подписью не бывает
 *    (он исчезает, стоит начать печатать);
 *  - подсказка и ошибка связаны через `aria-describedby`, ошибка — под полем и
 *    с `role="alert"`;
 *  - высота 36px, на мобиле не меньше 16px шрифта (иначе iOS зумит страницу);
 *  - у мутирующих полей нет автозаполнения браузером там, где оно вредит.
 */

const CONTROL =
  'w-full rounded-sm border bg-panel-2 px-3 py-2 text-base text-ink placeholder:text-ink-faint ' +
  'transition-colors duration-140 ease-[var(--ease-out-expo)] ' +
  'hover:border-line-strong focus:border-honey/60 ' +
  'disabled:cursor-not-allowed disabled:opacity-45 read-only:text-ink-muted'

interface ShellProps {
  label: string
  hint?: string
  error?: string | null
  required?: boolean
  className?: string
  children: (ids: { id: string; describedBy: string | undefined }) => ReactNode
}

export function FieldShell({
  label,
  hint,
  error,
  required = false,
  className,
  children
}: ShellProps): JSX.Element {
  const id = useId()
  const hintId = `${id}-hint`
  const errorId = `${id}-error`
  const describedBy =
    [hint === undefined ? null : hintId, error ? errorId : null].filter(Boolean).join(' ') ||
    undefined

  return (
    <div className={cn('flex min-w-0 flex-col gap-1.5', className)}>
      <label htmlFor={id} className="text-xs font-medium text-ink-muted">
        {label}
        {required ? (
          <span aria-hidden className="ml-1 text-honey">
            *
          </span>
        ) : null}
      </label>
      {children({ id, describedBy })}
      {hint === undefined ? null : (
        <p id={hintId} className="max-w-prose text-2xs text-ink-muted">
          {hint}
        </p>
      )}
      {error ? (
        <p id={errorId} role="alert" className="text-2xs text-fail">
          {error}
        </p>
      ) : null}
    </div>
  )
}

export interface TextFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> {
  label: string
  hint?: string
  error?: string | null
  mono?: boolean
}

export function TextField({
  label,
  hint,
  error,
  mono = false,
  className,
  required,
  ...rest
}: TextFieldProps): JSX.Element {
  return (
    <FieldShell label={label} hint={hint} error={error} required={required === true}>
      {({ id, describedBy }) => (
        <input
          id={id}
          aria-describedby={describedBy}
          aria-invalid={error ? true : undefined}
          required={required}
          className={cn(
            CONTROL,
            error ? 'border-fail' : 'border-line',
            mono && 'font-mono',
            className
          )}
          {...rest}
        />
      )}
    </FieldShell>
  )
}

export function NumberField(props: TextFieldProps): JSX.Element {
  // inputMode: numeric — на мобиле открывается цифровая клавиатура.
  return <TextField type="number" inputMode="numeric" mono {...props} />
}

export interface PasswordFieldProps extends Omit<TextFieldProps, 'type'> {
  label: string
}

export function PasswordField({ label, hint, error, ...rest }: PasswordFieldProps): JSX.Element {
  const [shown, setShown] = useState(false)
  return (
    <FieldShell label={label} hint={hint} error={error} required={rest.required === true}>
      {({ id, describedBy }) => (
        <div className="relative">
          <input
            id={id}
            type={shown ? 'text' : 'password'}
            aria-describedby={describedBy}
            aria-invalid={error ? true : undefined}
            className={cn(CONTROL, error ? 'border-fail' : 'border-line', 'pr-10')}
            {...rest}
          />
          <button
            type="button"
            onClick={() => setShown(previous => !previous)}
            aria-label={shown ? 'Скрыть пароль' : 'Показать пароль'}
            className="press absolute top-1/2 right-1 flex size-8 -translate-y-1/2 items-center justify-center rounded-xs text-ink-faint hover:text-ink"
          >
            {shown ? (
              <EyeOff aria-hidden className="size-4" />
            ) : (
              <Eye aria-hidden className="size-4" />
            )}
          </button>
        </div>
      )}
    </FieldShell>
  )
}

export interface TextAreaFieldProps extends Omit<
  TextareaHTMLAttributes<HTMLTextAreaElement>,
  'id'
> {
  label: string
  hint?: string
  error?: string | null
  mono?: boolean
}

export function TextAreaField({
  label,
  hint,
  error,
  mono = false,
  className,
  ...rest
}: TextAreaFieldProps): JSX.Element {
  return (
    <FieldShell label={label} hint={hint} error={error}>
      {({ id, describedBy }) => (
        <textarea
          id={id}
          aria-describedby={describedBy}
          aria-invalid={error ? true : undefined}
          className={cn(
            CONTROL,
            'min-h-24 resize-y',
            error ? 'border-fail' : 'border-line',
            mono && 'font-mono',
            className
          )}
          {...rest}
        />
      )}
    </FieldShell>
  )
}
